<template>
    <div class="visibility-changer">
        <!-- Mail temsilci görünürlük bölümü -->
        <div class="section" v-if="!!user.role.isRoot">
            <div class="section-header">
                <div class="title">{{ $t('mail.visibilityRepresentedMailsTitle') }}</div>
                <div class="switch-container">
                    <span class="label">{{ $t('mail.visibilitySeeOtherAgentsLabel') }}</span>
                    <a-switch v-model="isAgentMailVisible" size="small" @change="changeIsAgentMailVisible" />
                </div>
            </div>
        </div>

        <!-- Mail seçim bölümü -->
        <div class="section">
            <div class="section-header">
                <div class="title">Mail Adresi Seçimi</div>
            </div>
            <div class="section-content mail-selector">
                <select v-model="selectedMailId">
                    <option :value="mail._id" v-for="mail in mailAddresses" :key="mail._id">
                        {{ mail.mail }}
                    </option>
                </select>
            </div>
        </div>

        <!-- Görünürlük seçenekleri -->
        <div class="section">
            <div class="section-header">
                <div class="title">{{ $t('mail.visibilitySettingsTitle') }}</div>
            </div>
            <div class="visibility-options">
                <div
                    class="option"
                    :class="{ selected: visibilityType === 'public' }"
                    @click="
                        visibilityType = 'public';
                        handleVisibilityTypeChange();
                    "
                >
                    <div class="option-header">
                        <div class="radio">
                            <div class="inner" v-if="visibilityType === 'public'"></div>
                        </div>
                        <div class="option-title">{{ $t('mail.visibilityPublicTitle') }}</div>
                    </div>
                    <div class="option-content">{{ $t('mail.visibilityPublicDesc') }}</div>
                </div>

                <div
                    class="option"
                    :class="{ selected: visibilityType === 'restricted' }"
                    @click="
                        visibilityType = 'restricted';
                        handleVisibilityTypeChange();
                    "
                >
                    <div class="option-header">
                        <div class="radio">
                            <div class="inner" v-if="visibilityType === 'restricted'"></div>
                        </div>
                        <div class="option-title">{{ $t('mail.visibilityRestrictedTitle') }}</div>
                    </div>
                    <div class="option-content">
                        {{ $t('mail.visibilityRestrictedDesc') }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Kısıtlı erişim seçili ise gösterilecek olan temsilci ve departman panelleri -->
        <template v-if="visibilityType === 'restricted'">
            <!-- Temsilciler bölümü -->
            <div class="items-panel">
                <div class="panel-header">
                    <div class="panel-title">{{ $t('mail.visibilityAgentsCanViewTitle') }}</div>
                    <div class="custom-add-button" @click="openAgentModal">
                        <span class="icon">+</span>
                        <span class="text">{{ $t('mail.visibilityAdd') }}</span>
                    </div>
                </div>
                <div class="panel-content">
                    <div v-if="cloneUserIds.length === 0" class="empty-state">{{ $t('mail.visibilityNoAgentsSelected') }}</div>
                    <div v-else class="items-grid">
                        <div v-for="id of cloneUserIds" :key="id" class="item-card">
                            <div class="avatar-container">
                                <Avatar
                                    :size="36"
                                    :src="getUser(id).gravatar"
                                    :username="getUser(id).name"
                                    class="avatar"
                                />
                            </div>
                            <div class="item-details">
                                <div class="item-name">{{ getUser(id).name }}</div>
                            </div>
                            <div class="item-actions">
                                <SupsisButton icon="close" xxsmall @click="deleteAgent(id)" class="delete-btn" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Departmanlar bölümü -->
            <div class="items-panel">
                <div class="panel-header">
                    <div class="panel-title">{{ $t('mail.visibilityDepartmentsCanViewTitle') }}</div>
                    <div class="custom-add-button" @click="openDepartmentModal">
                        <span class="icon">+</span>
                        <span class="text">{{ $t('mail.visibilityAdd') }}</span>
                    </div>
                </div>
                <div class="panel-content">
                    <div v-if="cloneDepartmentIds.length === 0" class="empty-state">{{ $t('mail.visibilityNoDepartmentsSelected') }}</div>
                    <div v-else class="items-grid">
                        <div v-for="id of cloneDepartmentIds" :key="id" class="item-card">
                            <div class="avatar-container">
                                <Avatar
                                    :size="36"
                                    :src="undefined"
                                    :username="getDepartmentTitle(getDepartment(id))"
                                    class="avatar"
                                />
                            </div>
                            <div class="item-details">
                                <div class="item-name">{{ getDepartmentTitle(getDepartment(id)) }}</div>
                            </div>
                            <div class="item-actions">
                                <SupsisButton icon="close" xxsmall @click="deleteDepartment(id)" class="delete-btn" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <!-- Temsilci seçim modalı -->
        <Modal2
            :title="$t('mail.visibilitySelectAgentModalTitle')"
            :description="$t('mail.visibilitySelectAgentModalDescription')"
            v-if="visibleAgentModal"
            @cancel="cancelAgentModal"
            @ok="confirmAgentModal"
            :okText="$t('common.save')"
            :cancelText="$t('mail.cancel')"
            :className="agentModalClass"
            :showTitle="false"
            :showFooter="false"
        >
            <div class="modal-body">
                <div class="modal-header">
                    <div class="header-title">{{ $t('mail.visibilitySelectAgentModalTitle') }}</div>
                    <div class="header-description">{{ $t('mail.visibilitySelectAgentModalDescription') }}</div>
                </div>

                <div class="modal-search">
                    <input :placeholder="$t('mail.visibilitySearchAgentsPlaceholder')" v-model="searchText" />
                    <i class="material-icons search-icon">search</i>
                </div>

                <div class="select-all-option">
                    <a-checkbox :checked="tempSelectAllAgents" @change="toggleTempSelectAllAgents()">
                        {{ $t('mail.visibilitySelectAllAgents') }}
                    </a-checkbox>
                </div>

                <div class="items-list">
                    <div
                        class="item"
                        v-for="agent of getFilteredUsers()"
                        :key="agent._id"
                        @click="toggleTempAgent(agent._id)"
                    >
                        <a-checkbox class="checkbox" :checked="tempUserIds.includes(agent._id)" />
                        <div class="item-content">
                            <div class="user-info">
                                <Avatar :size="32" :src="agent.gravatar" :username="agent.name" />
                                <div class="user-details">
                                    <div class="name">{{ agent.name }}</div>
                                    <div class="description">{{ getAgentDepartmentNames(agent) }}</div>
                                </div>
                            </div>
                            <div class="status" :class="agent.status.toLowerCase()">
                                <div class="status-indicator"></div>
                                {{ agent.status }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal-footer">
                    <div class="button-group">
                        <div class="custom-button cancel" @click="cancelAgentModal">{{ $t('mail.cancel') }}</div>
                        <div class="custom-button primary" @click="confirmAgentModal">{{ $t('common.save') }}</div>
                    </div>
                </div>
            </div>
        </Modal2>

        <!-- Departman seçim modalı -->
        <Modal2
            :title="$t('mail.visibilitySelectDepartmentModalTitle')"
            :description="$t('mail.visibilitySelectDepartmentModalDescription')"
            v-if="visibleDepartmentModal"
            @cancel="cancelDepartmentModal"
            @ok="confirmDepartmentModal"
            :okText="$t('common.save')"
            :cancelText="$t('mail.cancel')"
            :className="departmentModalClass"
            :showTitle="false"
            :showFooter="false"
        >
            <div class="modal-body">
                <div class="modal-header">
                    <div class="header-title">{{ $t('mail.visibilitySelectDepartmentModalTitle') }}</div>
                    <div class="header-description">{{ $t('mail.visibilitySelectDepartmentModalDescription') }}</div>
                </div>

                <div class="modal-search">
                    <input :placeholder="$t('mail.visibilitySearchDepartmentsPlaceholder')" v-model="searchText" />
                    <i class="material-icons search-icon">search</i>
                </div>

                <div class="select-all-option">
                    <a-checkbox :checked="tempSelectAllDepartments" @change="toggleTempSelectAllDepartments()">
                        {{ $t('mail.visibilitySelectAllDepartments') }}
                    </a-checkbox>
                </div>

                <div class="items-list">
                    <div
                        class="item"
                        v-for="department of getFilteredDepartments()"
                        :key="department._id"
                        @click="toggleTempDepartment(department._id)"
                    >
                        <a-checkbox class="checkbox" :checked="tempDepartmentIds.includes(department._id)" />
                        <div class="item-content">
                            <div class="user-info">
                                <Avatar :size="32" :src="undefined" :username="getDepartmentTitle(department)" />
                                <div class="user-details">
                                    <div class="name">{{ getDepartmentTitle(department) }}</div>
                                    <div class="description">
                                        {{ $t('mail.visibilityAgentsCountInDept', { count: getDepartmentUsers(department._id).length }) }}
                                    </div>
                                </div>
                            </div>
                            <div class="avatar-group">
                                <Avatar
                                    v-for="user of getDepartmentUsers(department._id, 2)"
                                    :key="user._id"
                                    :size="24"
                                    :src="user.gravatar"
                                    :username="user.name"
                                    class="avatar-wrapper"
                                />
                                <div class="extra avatar-wrapper" v-if="getDepartmentUsers(department._id).length > 2">
                                    +{{ getDepartmentUsers(department._id, 2).length - 2 }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal-footer">
                    <div class="button-group">
                        <div class="custom-button cancel" @click="cancelDepartmentModal">{{ $t('mail.cancel') }}</div>
                        <div class="custom-button primary" @click="confirmDepartmentModal">{{ $t('common.save') }}</div>
                    </div>
                </div>
            </div>
        </Modal2>
    </div>
</template>
<script>
import Vue from "vue";
import { mapGetters, mapState, mapActions } from "vuex";
import Modal2 from "../../../../components/common/Modal2.vue";
import _ from "lodash";
import Avatar from "vue-avatar";
import SupsisButton from "../../Settings/components/SupsisButton.vue";

export default Vue.extend({
    name: "VisibilityChanger",
    components: { Modal2, Avatar, SupsisButton },
    props: {},
    data() {
        return {
            visibleAgentModal: false,
            visibleDepartmentModal: false,
            cloneUserIds: [],
            cloneDepartmentIds: [],
            tempUserIds: [], // Geçici kullanıcı seçimi
            tempDepartmentIds: [], // Geçici departman seçimi
            searchText: "",
            showTitle: true,
            selectedMailId: "",
            isAgentMailVisible: true,
            visibilityType: "public",
            isSaving: false,
            agentModalClass: "agent-modal",
            departmentModalClass: "department-modal",
            selectAllAgents: false,
            selectAllDepartments: false,
            tempSelectAllAgents: false, // Geçici tüm temsilcileri seçme durumu
            tempSelectAllDepartments: false, // Geçici tüm departmanları seçme durumu
            mailAddresses: [],
        };
    },
    computed: {
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
        ...mapState({
            users: (state) => state.user.users.data,
            user: (state) => state.auth.user,
            departments: (state) => state.subject.subjects.data,
            site: (state) => state.site.site,
            allMailAddresses: (state) => state.mail.allMailAddresses,
        }),
    },
    methods: {
        ...mapActions({
            changeVisibility: "mail/changeVisibility",
            getVisibilityUser: "mail/getVisibilityUser",
            changeIsAgentMailVisibleAction: "mail/changeIsAgentMailVisible",
            getAgentMailVisibility: "mail/getAgentMailVisibility",
            getSenderMailAddress: "mail/getSenderMailAddress",
        }),
        getMailAddress() {
            const selectedMail = this.allMailAddresses.find((x) => x._id === this.selectedMailId);
            if (selectedMail) {
                this.getSenderMailAddress({
                    userId: this.user._id,
                    selectedMailAddressId: selectedMail._id,
                    forwardTo: selectedMail.mail,
                }).then(({ mailAddresses }) => {
                    this.mailAddresses = mailAddresses;
                });
            }
        },
        async changeIsAgentMailVisible() {
            await this.changeIsAgentMailVisibleAction({ userId: this.user._id, visible: this.isAgentMailVisible });
            this.$message.success(this.$t("mail.visibilityAgentMailSettingSaved"));
        },
        getDepartmentTitle(department) {
            if (department === undefined) return this.$t("mail.notApplicableShort");
            const langs = Object.keys(department?.languageContent || {});
            const targetLang = langs.length > 0 ? langs[0] : "";
            return _.get(department, `languageContent.${targetLang}.name`);
        },
        getUser(userId) {
            return this.users.find((user) => user._id === userId);
        },
        getIsPublic() {
            return this.visibilityType === "public";
        },
        getDepartment(id) {
            return this.departments.find((department) => department._id === id);
        },
        getFilteredUsers() {
            let q = this.searchText.split("").join(".*");
            return this.users.filter((x) => new RegExp(q, "gi").test(x.name));
        },
        getFilteredDepartments() {
            let q = this.searchText.split("").join(".*");
            return this.departments.filter((x) => new RegExp(q, "gi").test(x.name));
        },
        deleteAgent(agentId) {
            this.cloneUserIds = this.cloneUserIds.filter((x) => x !== agentId);
            this.handleSave();
        },
        deleteDepartment(departmentId) {
            this.cloneDepartmentIds = this.cloneDepartmentIds.filter((x) => x !== departmentId);
            this.handleSave();
        },
        // Geçici temsilci seçimi
        toggleTempAgent(agentId) {
            this.tempUserIds = this.tempUserIds.includes(agentId)
                ? this.tempUserIds.filter((x) => x !== agentId)
                : [...this.tempUserIds, agentId];

            // Tüm ajanlar seçili mi kontrol et
            this.tempSelectAllAgents = this.users.length === this.tempUserIds.length;
        },
        // Geçici departman seçimi
        toggleTempDepartment(departmentId) {
            this.tempDepartmentIds = this.tempDepartmentIds.includes(departmentId)
                ? this.tempDepartmentIds.filter((x) => x !== departmentId)
                : [...this.tempDepartmentIds, departmentId];

            // Tüm departmanlar seçili mi kontrol et
            this.tempSelectAllDepartments = this.departments.length === this.tempDepartmentIds.length;
        },
        // Geçici tüm temsilcileri seçme
        toggleTempSelectAllAgents() {
            if (this.tempSelectAllAgents) {
                // Tüm ajanları seç
                this.tempUserIds = this.users.map((user) => user._id);
            } else {
                // Seçimleri temizle
                this.tempUserIds = [];
            }
        },
        // Geçici tüm departmanları seçme
        toggleTempSelectAllDepartments() {
            if (this.tempSelectAllDepartments) {
                // Tüm departmanları seç
                this.tempDepartmentIds = this.departments.map((dept) => dept._id);
            } else {
                // Seçimleri temizle
                this.tempDepartmentIds = [];
            }
        },
        openAgentModal() {
            // Modal açıldığında geçici listeyi mevcut seçimlerle doldur
            this.tempUserIds = [...this.cloneUserIds];
            this.tempSelectAllAgents = this.users.length === this.tempUserIds.length && this.users.length > 0;
            this.visibleAgentModal = true;
        },
        openDepartmentModal() {
            // Modal açıldığında geçici listeyi mevcut seçimlerle doldur
            this.tempDepartmentIds = [...this.cloneDepartmentIds];
            this.tempSelectAllDepartments =
                this.departments.length === this.tempDepartmentIds.length && this.departments.length > 0;
            this.visibleDepartmentModal = true;
        },
        cancelAgentModal() {
            // İptal durumunda geçici listeyi temizle
            this.tempUserIds = [];
            this.visibleAgentModal = false;
        },
        cancelDepartmentModal() {
            // İptal durumunda geçici listeyi temizle
            this.tempDepartmentIds = [];
            this.visibleDepartmentModal = false;
        },
        confirmAgentModal() {
            // Onay durumunda geçici listedeki seçimleri asıl listeye aktar
            this.cloneUserIds = [...this.tempUserIds];
            this.selectAllAgents = this.tempSelectAllAgents;
            this.visibleAgentModal = false;
            this.handleSave();
        },
        confirmDepartmentModal() {
            // Onay durumunda geçici listedeki seçimleri asıl listeye aktar
            this.cloneDepartmentIds = [...this.tempDepartmentIds];
            this.selectAllDepartments = this.tempSelectAllDepartments;
            this.visibleDepartmentModal = false;
            this.handleSave();
        },
        async handleSave() {
            if (this.isSaving) return;

            this.isSaving = true;
            try {
                const currentMail = this.allMailAddresses.find((x) => x._id === this.selectedMailId);
                if (!currentMail) {
                    this.isSaving = false;
                    return;
                }

                await this.changeVisibility({
                    mail: currentMail.mail,
                    id: this.user._id,
                    visibility: { users: this.cloneUserIds, departments: this.cloneDepartmentIds },
                });

                this.$message.success(this.$t("mail.visibilitySettingsSavedSuccess"));
            } catch (error) {
                this.$message.error(this.$t("mail.visibilitySettingsSaveError"));
                console.error("Görünürlük ayarları kaydedilirken hata:", error);
            } finally {
                this.isSaving = false;
            }
        },
        handleVisibilityTypeChange() {
            if (this.visibilityType === "public") {
                // Public seçildiğinde kullanıcı ve departman listelerini temizle
                this.cloneUserIds = [];
                this.cloneDepartmentIds = [];
                this.handleSave();
            }
        },
        getAgentDepartmentNames(user) {
            return (user.subjects || []).length
                ? this.departments
                      .filter((x) => (user.subjects || []).includes(x._id))
                      .map((x) => _.get(x, `languageContent.${Object.keys(x.languageContent)[0]}.name`))
                      .join(",")
                      .toLowerCase()
                : this.$t("mail.visibilityAllDepartmentsFallback");
        },
        getDepartmentUsers(departmentId, take) {
            const users = this.users.filter((x) => (x.subjects || []).includes(departmentId));
            if (take) {
                return users.slice(0, take);
            } else {
                return users;
            }
        },
    },
    async mounted() {
        this.getAgentMailVisibility(this.user._id).then((res) => {
            console.log("visibilityList res...", res);
            this.isAgentMailVisible = res.agentMailVisibility;
            console.log("visibilityList isAgentMailVisible...", this.isAgentMailVisible);
        });
        this.selectedMailId = this.$route.params.id;
        const mailAddress = await this.getVisibilityUser(this.$route.params.id);
        console.log("visibilityList work", mailAddress);
        this.cloneUserIds = mailAddress ? mailAddress.visibility.users : [];
        this.cloneDepartmentIds = mailAddress ? mailAddress.visibility.departments : [];

        this.visibilityType =
            this.cloneUserIds.length === 0 && this.cloneDepartmentIds.length === 0 ? "public" : "restricted";

        // Tümü seçili mi durumlarını güncelle
        this.selectAllAgents = this.users.length === this.cloneUserIds.length && this.users.length > 0;
        this.selectAllDepartments =
            this.departments.length === this.cloneDepartmentIds.length && this.departments.length > 0;

        this.getMailAddress();
    },
    watch: {
        async selectedMailId(val) {
            const mailAddress = await this.getVisibilityUser(val);
            this.cloneUserIds = mailAddress ? mailAddress.visibility.users : [];
            this.cloneDepartmentIds = mailAddress ? mailAddress.visibility.departments : [];

            this.visibilityType =
                this.cloneUserIds.length === 0 && this.cloneDepartmentIds.length === 0 ? "public" : "restricted";

            // Tümü seçili mi durumlarını güncelle
            this.selectAllAgents = this.users.length === this.cloneUserIds.length && this.users.length > 0;
            this.selectAllDepartments =
                this.departments.length === this.cloneDepartmentIds.length && this.departments.length > 0;
        },
        allMailAddresses: {
            deep: true,
            handler() {
                this.getMailAddress();
            },
        }
    },
});
</script>

<style scoped lang="scss">
.visibility-changer {
    display: flex;
    flex-direction: column;
    gap: 24px;
    background-color: #fff;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    .section {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 10px;
            border-bottom: 1px solid #eaedf0;

            .title {
                font-size: 16px;
                font-weight: 600;
                color: #333;
            }

            .switch-container {
                display: flex;
                align-items: center;
                gap: 8px;

                .label {
                    font-size: 14px;
                    color: #555;
                }
            }
        }

        .section-content {
            &.mail-selector {
                select {
                    width: 100%;
                    padding: 10px 12px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    background-color: #fff;
                    font-size: 14px;
                    color: #333;
                    transition: all 0.2s;

                    &:focus {
                        border-color: #3699ff;
                        box-shadow: 0 0 0 2px rgba(54, 153, 255, 0.1);
                    }
                }
            }
        }
    }

    .visibility-options {
        display: flex;
        gap: 12px;
        margin-bottom: 10px;

        .option {
            flex: 1;
            background-color: #fafafa;
            border: 1px solid #eaedf0;
            border-radius: 8px;
            padding: 14px;
            cursor: pointer;
            transition: all 0.2s;

            .option-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 10px;

                .radio {
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    border: 2px solid #ddd;
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    .inner {
                        width: 10px;
                        height: 10px;
                        border-radius: 50%;
                        background-color: #22272d;
                    }
                }

                .option-title {
                    font-weight: 600;
                    font-size: 14px;
                    color: #333;
                }
            }

            .option-content {
                color: #666;
                font-size: 13px;
                line-height: 1.4;
            }

            &.selected {
                background-color: #f0f5fa;
                border-color: #d0dbe9;

                .radio {
                    border-color: #22272d;
                }
            }
        }
    }

    .items-panel {
        border: 1px solid #eaedf0;
        border-radius: 8px;
        overflow: hidden;

        .panel-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 14px 16px;
            background-color: #f9fafb;
            border-bottom: 1px solid #eaedf0;

            .panel-title {
                font-weight: 600;
                font-size: 14px;
                color: #333;
            }

            .custom-add-button {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                background-color: #22272d;
                color: white;
                padding: 8px 16px;
                border-radius: 4px;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s ease;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

                .icon {
                    font-size: 16px;
                    font-weight: bold;
                }

                .text {
                    font-size: 14px;
                }

                &:hover {
                    background-color: #2c343c;
                    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
                    transform: translateY(-1px);
                }

                &:active {
                    transform: translateY(0);
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
                }
            }
        }

        .panel-content {
            padding: 16px;
            min-height: 100px;
            background-color: #fff;

            .empty-state {
                height: 80px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 14px;
                color: #999;
            }

            .items-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
                gap: 12px;

                .item-card {
                    display: flex;
                    gap: 10px;
                    align-items: center;
                    padding: 10px;
                    border-radius: 6px;
                    background-color: #f9fafb;
                    transition: all 0.2s;

                    &:hover {
                        background-color: #f0f7ff;
                    }

                    .avatar-container {
                        .avatar {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                        }
                    }

                    .item-details {
                        flex: 1;
                        min-width: 0;

                        .item-name {
                            font-size: 13px;
                            font-weight: 500;
                            color: #333;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }
                    }

                    .item-actions {
                        .delete-btn {
                            width: 24px;
                            height: 24px;
                            padding: 0;
                            min-width: unset;
                            border-radius: 4px;

                            i {
                                font-size: 18px;
                                color: #666;
                            }

                            &:hover {
                                background-color: rgba(244, 67, 54, 0.1);

                                i {
                                    color: #f44336;
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    .bottom-actions {
        display: flex;
        justify-content: flex-end;
        padding-top: 16px;
        margin-top: 16px;
        border-top: 1px solid #eaedf0;

        .custom-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 150px;
            padding: 12px 24px;
            border-radius: 4px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;

            &.primary {
                background-color: #22272d;
                color: white;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

                &:hover {
                    background-color: #2c343c;
                    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
                    transform: translateY(-1px);
                }

                &:active {
                    transform: translateY(0);
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
                }
            }

            &.large {
                font-size: 15px;
                padding: 12px 28px;
            }

            &.disabled {
                opacity: 0.7;
                cursor: not-allowed;
                pointer-events: none;
            }
        }
    }
}
</style>

<style lang="scss">
.visibility-avatar {
    span {
        color: #fff;
    }
}

.agent-modal,
.department-modal {
    .ant-modal-body {
        .modal-body {
            padding: 20px;
            display: flex;
            flex-direction: column;
            gap: 16px;

            .modal-header {
                margin-bottom: 8px;

                .header-title {
                    font-size: 18px;
                    font-weight: 600;
                    color: #333;
                    margin-bottom: 4px;
                }

                .header-description {
                    font-size: 14px;
                    color: #666;
                }
            }

            .modal-footer {
                margin-top: 16px;
                padding-top: 16px;
                border-top: 1px solid #eaedf0;

                .button-group {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;

                    .custom-button {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        min-width: 100px;
                        padding: 10px 16px;
                        border-radius: 4px;
                        font-size: 14px;
                        font-weight: 500;
                        cursor: pointer;
                        transition: all 0.2s ease;

                        &.primary {
                            background-color: #22272d;
                            color: white;
                            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

                            &:hover {
                                background-color: #2c343c;
                                box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
                            }

                            &:active {
                                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
                            }
                        }

                        &.cancel {
                            background-color: #f5f5f5;
                            color: #333;
                            border: 1px solid #ddd;

                            &:hover {
                                background-color: #e8e8e8;
                            }
                        }
                    }
                }
            }
        }
    }

    .modal-search {
        position: relative;
        margin-bottom: 0;

        input {
            width: 100%;
            padding: 10px 12px;
            padding-left: 36px;
            border: 1px solid #ddd;
            border-radius: 6px;
            font-size: 14px;

            &:focus {
                border-color: #22272d;
                outline: none;
                box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.1);
            }
        }

        .search-icon {
            position: absolute;
            left: 12px;
            top: 50%;
            transform: translateY(-50%);
            color: #999;
            font-size: 18px;
        }
    }

    .select-all-option {
        margin-bottom: 0;
        padding: 12px 16px;
        background-color: #f5f8fa;
        border-radius: 6px;
        font-weight: 500;

        .ant-checkbox-wrapper {
            font-size: 14px;
            color: #333;
            font-weight: 600;

            .ant-checkbox-checked .ant-checkbox-inner {
                background-color: #22272d;
                border-color: #22272d;
            }
        }
    }

    .items-list {
        max-height: 400px;
        overflow-y: auto;
        border: 1px solid #eaedf0;
        border-radius: 8px;

        .item {
            display: flex;
            align-items: center;
            padding: 10px 12px;
            border-bottom: 1px solid #eaedf0;
            cursor: pointer;
            transition: background-color 0.2s;

            &:last-child {
                border-bottom: none;
            }

            &:hover {
                background-color: #f5f8fa;
            }

            .checkbox {
                margin-right: 10px;

                .ant-checkbox-checked .ant-checkbox-inner {
                    background-color: #22272d;
                    border-color: #22272d;
                }
            }

            .item-content {
                flex: 1;
                display: flex;
                justify-content: space-between;
                align-items: center;

                .user-info {
                    display: flex;
                    align-items: center;
                    gap: 10px;

                    .user-details {
                        .name {
                            font-size: 14px;
                            font-weight: 500;
                            color: #333;
                        }

                        .description {
                            font-size: 12px;
                            color: #777;
                        }
                    }
                }

                .status {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 12px;

                    .status-indicator {
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;
                    }

                    &.online .status-indicator {
                        background-color: #1bc5bd;
                    }

                    &.away .status-indicator {
                        background-color: #3699ff;
                    }

                    &.busy .status-indicator {
                        background-color: #ffa800;
                    }

                    &.offline .status-indicator {
                        background-color: #f64e60;
                    }
                }
            }
        }
    }

    .avatar-wrapper {
        margin-inline-start: -8px;

        &.extra {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            border-color: white;
            color: rgb(245, 106, 0);
            background-color: rgb(253, 227, 207);
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 10px;
        }

        &:first-child {
            margin-inline-start: unset;
        }
    }
}
</style>
